'use client';

/**
 * RequestAccessModal Component
 *
 * Modal displayed when users attempt to access permission-locked features.
 * Provides option to request access from admin.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import { Box, Dialog, DialogContent, IconButton, TextField, Typography, useTheme } from '@mui/material';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassAlert } from '../GlassAlert/GlassAlert';
import { GlassButton } from '../GlassButton/GlassButton';

// ============================================================================
// Types
// ============================================================================

export interface RequestAccessModalProps {
  /** Whether the modal is open */
  open: boolean;

  /** Handler to close the modal */
  onClose: () => void;

  /** Feature that requires access */
  feature?: string;

  /** Required permission */
  requiredPermission?: string;

  /** Required license */
  requiredLicense?: string;

  /** Handler for access request submission */
  onRequestAccess?: (request: AccessRequest) => void | Promise<void>;

  /** Admin contact email (shown if no onRequestAccess) */
  adminEmail?: string;

  /** Whether to show the reason textarea */
  showReasonField?: boolean;
}

export interface AccessRequest {
  /** Feature being requested */
  feature?: string;

  /** Permission being requested */
  permission?: string;

  /** License being requested */
  license?: string;

  /** Optional reason/justification */
  reason?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * RequestAccessModal
 *
 * Shows access request form when users try to access permission-locked features.
 *
 * @example
 * ```tsx
 * <RequestAccessModal
 *   open={showRequest}
 *   onClose={() => setShowRequest(false)}
 *   feature="Delete Documents"
 *   requiredPermission="documents:delete"
 *   onRequestAccess={async (request) => {
 *     await api.requestAccess(request);
 *   }}
 * />
 * ```
 */
export function RequestAccessModal({
  open,
  onClose,
  feature,
  requiredPermission,
  requiredLicense,
  onRequestAccess,
  adminEmail,
  showReasonField = true,
}: RequestAccessModalProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!onRequestAccess) {
      // If no handler, just show success message
      setSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onRequestAccess({
        feature,
        permission: requiredPermission,
        license: requiredLicense,
        reason: reason.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  }, [onRequestAccess, feature, requiredPermission, requiredLicense, reason]);

  const handleClose = useCallback(() => {
    setReason('');
    setSubmitted(false);
    setError(null);
    onClose();
  }, [onClose]);

  // Determine what type of access is needed
  const accessType = requiredLicense ? 'license' : 'permission';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            pt: 4,
            pb: 3,
            px: 4,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(144,202,249,0.1) 0%, rgba(66,165,245,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(144,202,249,0.15) 0%, rgba(66,165,245,0.08) 100%)',
          }}
        >
          {submitted ? (
            <>
              <SendIcon
                sx={{
                  fontSize: iconSizes['3xl'],
                  color: theme.palette.success.main,
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('requestAccess.submitted')}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('requestAccess.submittedDescription')}
              </Typography>
            </>
          ) : (
            <>
              <LockIcon
                sx={{
                  fontSize: iconSizes['3xl'],
                  color: theme.palette.action.disabled,
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('requestAccess.title')}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                {feature ? t('requestAccess.featureDescription', { feature }) : t('requestAccess.generalDescription')}
              </Typography>
            </>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ px: 4, py: 3 }}>
          {submitted ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  marginBottom: '16px',
                }}
              >
                {adminEmail
                  ? t('requestAccess.contactAdmin', { email: adminEmail })
                  : t('requestAccess.adminWillReview')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Info about what's needed */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <AdminPanelSettingsIcon color="action" />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {accessType === 'license'
                      ? t('requestAccess.licenseRequired')
                      : t('requestAccess.permissionRequired')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {accessType === 'license'
                      ? t('requestAccess.licenseExplanation', {
                          license: requiredLicense,
                        })
                      : t('requestAccess.permissionExplanation')}
                  </Typography>
                </Box>
              </Box>

              {/* Reason field */}
              {showReasonField && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('requestAccess.reasonLabel')}
                  placeholder={t('requestAccess.reasonPlaceholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {/* Error message */}
              {error && (
                <GlassAlert severity="error" sx={{ mb: 2 }}>
                  {error}
                </GlassAlert>
              )}

              {/* Admin contact info */}
              {adminEmail && !onRequestAccess && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  {t('requestAccess.orContactAdmin', { email: adminEmail })}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {submitted ? (
            <GlassButton variant="gradient" onClick={handleClose}>
              {t('common.close')}
            </GlassButton>
          ) : (
            <>
              <GlassButton variant="subtle" onClick={handleClose}>
                {t('common.cancel')}
              </GlassButton>
              {onRequestAccess && (
                <GlassButton variant="gradient" onClick={handleSubmit} loading={isSubmitting} startIcon={<SendIcon />}>
                  {t('requestAccess.submitRequest')}
                </GlassButton>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Context for programmatic usage
// ============================================================================

interface RequestAccessModalContextValue {
  /** Open the request access modal */
  openRequestAccessModal: (props: Omit<RequestAccessModalProps, 'open' | 'onClose'>) => void;

  /** Close the modal */
  closeRequestAccessModal: () => void;

  /** Whether the modal is currently open */
  isOpen: boolean;
}

const RequestAccessModalContext = createContext<RequestAccessModalContextValue | null>(null);

/**
 * Provider for programmatic request access modal
 */
export function RequestAccessModalProvider({
  children,
  onRequestAccess,
  adminEmail,
}: {
  children: React.ReactNode;
  onRequestAccess?: RequestAccessModalProps['onRequestAccess'];
  adminEmail?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState<Omit<RequestAccessModalProps, 'open' | 'onClose'>>({});

  const openRequestAccessModal = useCallback(
    (props: Omit<RequestAccessModalProps, 'open' | 'onClose'>) => {
      setModalProps({
        ...props,
        onRequestAccess: props.onRequestAccess ?? onRequestAccess,
        adminEmail: props.adminEmail ?? adminEmail,
      });
      setIsOpen(true);
    },
    [onRequestAccess, adminEmail],
  );

  const closeRequestAccessModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      openRequestAccessModal,
      closeRequestAccessModal,
      isOpen,
    }),
    [openRequestAccessModal, closeRequestAccessModal, isOpen],
  );

  return (
    <RequestAccessModalContext.Provider value={value}>
      {children}
      <RequestAccessModal open={isOpen} onClose={closeRequestAccessModal} {...modalProps} />
    </RequestAccessModalContext.Provider>
  );
}

/**
 * Hook to access request access modal context
 */
export function useRequestAccessModal(): RequestAccessModalContextValue {
  const context = useContext(RequestAccessModalContext);

  if (!context) {
    throw new Error('useRequestAccessModal must be used within a RequestAccessModalProvider');
  }

  return context;
}

/**
 * Optional hook (returns null if not in provider)
 */
export function useRequestAccessModalOptional(): RequestAccessModalContextValue | null {
  return useContext(RequestAccessModalContext);
}

export default RequestAccessModal;
